import { useSyncExternalStore } from 'react';
import { authService } from '@/lib/authService';
import { emit } from '@/lib/typedEventBus';
import { BILLING_SUBMISSION_STATUS_LABELS } from '../work-items/billingSubmissionTypes';
import type { BillingSubmissionStatus } from '../work-items/billingSubmissionTypes';
import type {
  BillingDashboardScopeType,
  BillingSectionId,
  EffectiveJobStatusDetails,
  JobFinancialInsights,
  TeamScopeSnapshot,
} from './billingDashboardTypes';

export function normalizeIds(ids?: string[] | string | null): string[] {
  if (typeof ids === 'string') {
    return Array.from(new Set(ids.split(',').map((value) => value.trim()).filter(Boolean)));
  }

  if (Array.isArray(ids)) {
    return Array.from(new Set(
      ids
        .flatMap((value) => typeof value === 'string' ? value.split(',') : [])
        .map((value) => value.trim())
        .filter(Boolean),
    ));
  }

  return [];
}

export function getScopeType(workItemType?: string, hasRootIds?: boolean): BillingDashboardScopeType {
  if (!hasRootIds) {
    return 'aggregate';
  }

  if (workItemType === 'appointment') return 'appointment';
  if (workItemType === 'task') return 'task';
  if (workItemType === 'project') return 'project';
  return 'job';
}

export const formatCurrency = (amount: number): string =>
  'R ' + new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount || 0);

export const formatHours = (hours: number): string => `${(hours || 0).toFixed(1)}h`;

const BILLING_CHARGE_TOGGLES_KEY = 'billing-charge-toggles';
const TIMER_AGGREGATION_MODE_KEY = 'timer-aggregation-mode';

export type TimerAggregationMode = 'individual' | 'team';

const TIMER_AGGREGATION_MODE_EVENT = 'timer-aggregation-mode-changed';

export function loadTimerAggregationMode(): TimerAggregationMode {
  if (typeof window === 'undefined') return 'individual';
  try {
    const raw = localStorage.getItem(TIMER_AGGREGATION_MODE_KEY);
    if (raw === 'team' || raw === 'individual') return raw;
  } catch { /* ignore */ }
  return 'individual';
}

export function saveTimerAggregationMode(mode: TimerAggregationMode): void {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem(TIMER_AGGREGATION_MODE_KEY, mode);
    // `storage` events do not fire in the tab that wrote the value, so dispatch
    // a custom event to notify same-tab subscribers (e.g. a parent modal that
    // stays mounted while a nested modal changes the mode).
    window.dispatchEvent(new Event(TIMER_AGGREGATION_MODE_EVENT));
  } catch { /* ignore */ }
}

export function normalizeTimerAggregationMode(value: unknown): TimerAggregationMode | null {
  return value === 'team' || value === 'individual' ? value : null;
}

/** Reads `timer_aggregation_mode` from a work item's custom_fields (object or JSON string). */
export function getTimerAggregationModeFromCustomFields(customFields: unknown): TimerAggregationMode | null {
  let value: unknown;
  if (typeof customFields === 'string') {
    try {
      value = (JSON.parse(customFields) as Record<string, unknown>)?.timer_aggregation_mode;
    } catch {
      return null;
    }
  } else if (customFields && typeof customFields === 'object') {
    value = (customFields as Record<string, unknown>).timer_aggregation_mode;
  }
  return normalizeTimerAggregationMode(value);
}

/**
 * Persists the chosen timer aggregation mode on the work item itself
 * (`custom_fields.timer_aggregation_mode`) so report generation, billing
 * submissions, and other sessions resolve the same per-item setting instead of
 * relying on browser-local state alone. Fire-and-forget: failures are logged,
 * never thrown — the toggle still applies locally.
 */
export function persistTimerAggregationModeForWorkItem(
  workItemId: string | number | null | undefined,
  mode: TimerAggregationMode,
): void {
  if (typeof window === 'undefined' || workItemId == null || workItemId === '') return;
  const token = authService.getTokens()?.access_token;
  void fetch(`/api/v1/work-items/${encodeURIComponent(String(workItemId))}`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify({ 'custom_fields.timer_aggregation_mode': mode }),
  }).then((res) => {
    if (!res.ok) {
      console.warn(`[TimerAggregation] Failed to persist mode '${mode}' on work item ${workItemId} (${res.status})`);
    }
  }).catch((error) => {
    console.warn('[TimerAggregation] Persist error:', error);
  });
}

function subscribeTimerAggregationMode(callback: () => void): () => void {
  window.addEventListener(TIMER_AGGREGATION_MODE_EVENT, callback);
  window.addEventListener('storage', callback);
  return () => {
    window.removeEventListener(TIMER_AGGREGATION_MODE_EVENT, callback);
    window.removeEventListener('storage', callback);
  };
}

/**
 * Live view of the timer aggregation mode. Unlike a mount-time localStorage
 * read, this re-renders when any component (including nested work-item modals)
 * or another browser tab changes the stored mode.
 */
export function useTimerAggregationMode(): TimerAggregationMode {
  return useSyncExternalStore(
    subscribeTimerAggregationMode,
    loadTimerAggregationMode,
    () => 'individual',
  );
}

export type ChargeToggles = {
  travel_km: boolean;
  travel_time: boolean;
  onsite_labour: boolean;
};

export function loadChargeTogglesFromStorage(): ChargeToggles | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = localStorage.getItem(BILLING_CHARGE_TOGGLES_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === 'object') {
      return {
        travel_km: parsed.travel_km !== false,
        travel_time: parsed.travel_time !== false,
        onsite_labour: parsed.onsite_labour !== false,
      };
    }
  } catch { /* ignore */ }
  return null;
}

export function saveChargeTogglesToStorage(toggles: ChargeToggles): void {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem(BILLING_CHARGE_TOGGLES_KEY, JSON.stringify(toggles));
  } catch { /* ignore */ }
}

const FINANCE_WORKSPACE_KEYWORDS = [
  'finance',
  'accounts',
  'accounting',
  'billing',
  'debtor',
  'creditor',
  'bookkeep',
  'bookkeeper',
] as const;

function normalizeWorkspaceValue(value: unknown): string {
  if (typeof value === 'string') {
    return value.trim().toLowerCase();
  }

  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value).trim().toLowerCase();
  }

  return '';
}

function humanizeWorkspaceValue(value: string): string {
  if (!value) {
    return '';
  }

  return value
    .split(/[_\s-]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

export function getFinanceWorkspaceAccess() {
  const user = authService.getUser() as (Record<string, unknown> & { department?: string }) | null;
  const permissions = user?.permissions;
  const permissionsRole = permissions && typeof permissions === 'object'
    ? (permissions as Record<string, unknown>).role
    : undefined;
  const roleCandidates = [
    user?.role,
    user?.active_client_role,
    permissionsRole,
  ]
    .map(normalizeWorkspaceValue)
    .filter(Boolean);
  const department = normalizeWorkspaceValue(user?.department);
  const isAdminLike = roleCandidates.some((role) => ['admin', 'owner', 'root'].includes(role));
  const hasFinanceKeyword = (value: string) => FINANCE_WORKSPACE_KEYWORDS.some((keyword) => value.includes(keyword));
  const isFinanceLike = isAdminLike || roleCandidates.some(hasFinanceKeyword) || hasFinanceKeyword(department);

  return {
    isFinanceLike,
    isAdminLike,
    departmentLabel: humanizeWorkspaceValue(department),
    primaryRoleLabel: humanizeWorkspaceValue(roleCandidates[0] || ''),
  };
}

export const getAuthHeaders = () => {
  const token = authService.getTokens()?.access_token;
  return {
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
  };
};

export type WorkItemApiResponse = {
  success?: boolean;
  data?: {
    id?: string;
    item_number?: string;
    title?: string;
    item_type?: string;
    item_subtype?: string;
    contact_id?: number | null;
    contact_name?: string | null;
    status?: string;
    assigned_to?: number | string | null;
    custom_fields?: Record<string, unknown> | null;
    assigned_users_details?: Array<{
      user_id?: number | string | null;
      first_name?: string | null;
      last_name?: string | null;
      email?: string | null;
    }>;
    location_id?: number | null;
    location_name?: string | null;
    location_address?: string | null;
    contact_person_id?: number | null;
    contact_person_name?: string | null;
  };
};

export async function fetchJson<T>(input: RequestInfo | URL, init?: RequestInit): Promise<T | null> {
  const response = await fetch(input, init);
  if (!response.ok) {
    return null;
  }

  return response.json() as Promise<T>;
}

/** Run `mapper` over `items` with at most `concurrency` tasks in-flight at once. */
export async function pMap<T, R>(items: T[], mapper: (item: T) => Promise<R>, concurrency = 4): Promise<R[]> {
  const results: R[] = new Array(items.length);
  let next = 0;
  const run = async (): Promise<void> => {
    while (next < items.length) {
      const idx = next++;
      results[idx] = await mapper(items[idx]);
    }
  };
  await Promise.all(Array.from({ length: Math.min(concurrency, items.length) }, run));
  return results;
}

function normalizeDedupeKey(value: unknown): string {
  if (typeof value === 'string') {
    return value.trim();
  }

  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }

  return '';
}

export function dedupeRecords<T>(items: T[], getKey: (item: T, index: number) => unknown): T[] {
  const seen = new Set<string>();
  const deduped: T[] = [];

  items.forEach((item, index) => {
    const key = normalizeDedupeKey(getKey(item, index));

    if (!key) {
      deduped.push(item);
      return;
    }

    if (seen.has(key)) {
      return;
    }

    seen.add(key);
    deduped.push(item);
  });

  return deduped;
}

export function sanitizeJobFinancialInsights(insights: JobFinancialInsights): JobFinancialInsights {
  return {
    ...insights,
    time_summary: {
      ...insights.time_summary,
      by_person: dedupeRecords(insights.time_summary.by_person || [], (person, index) => person.user_id || `${person.user_name}:${index}`),
    },
    source_breakdown: dedupeRecords(insights.source_breakdown || [], (source, index) => (
      source.id || source.item_number || `${source.item_type}:${source.title}:${index}`
    )),
    documents: dedupeRecords(insights.documents || [], (document, index) => (
      document.id || document.item_number || `${document.item_type}:${document.title}:${index}`
    )).map((document) => ({
      ...document,
      linked_items: dedupeRecords(document.linked_items || [], (item, index) => (
        item.id || `${item.item_type}:${item.item_number}:${index}`
      )),
    })),
  };
}

const COMPLETED_WORK_ITEM_STATUSES = new Set(['completed', 'closed', 'resolved', 'done']);
const CANCELLED_WORK_ITEM_STATUSES = new Set(['cancelled', 'canceled', 'void']);
const ACTIVE_APPOINTMENT_SOURCE_STATUSES = new Set(['in_progress', 'checked_in', 'working', 'paused', 'on_site', 'on_route', 'arrived']);

function normalizeBillingStatusValue(value?: string | null): string {
  return typeof value === 'string'
    ? value.trim().toLowerCase().replace(/[\s-]+/g, '_')
    : '';
}

export function isCompletedWorkItemStatus(value?: string | null): boolean {
  return COMPLETED_WORK_ITEM_STATUSES.has(normalizeBillingStatusValue(value));
}

export function isCancelledWorkItemStatus(value?: string | null): boolean {
  return CANCELLED_WORK_ITEM_STATUSES.has(normalizeBillingStatusValue(value));
}

export function deriveEffectiveJobStatus(
  sourceStatus?: string,
  sourceBreakdown?: JobFinancialInsights['source_breakdown'] | null,
): EffectiveJobStatusDetails {
  const appointmentSources = Array.isArray(sourceBreakdown)
    ? sourceBreakdown.filter((source) => source?.item_type === 'appointment')
    : [];
  const activeAppointmentCount = appointmentSources.filter((source) => (
    ACTIVE_APPOINTMENT_SOURCE_STATUSES.has(normalizeBillingStatusValue(source?.status))
  )).length;

  let nextStatus = sourceStatus;
  let derivedFromAppointments = false;

  if (
    activeAppointmentCount > 0
    && !isCompletedWorkItemStatus(sourceStatus)
    && !isCancelledWorkItemStatus(sourceStatus)
    && normalizeBillingStatusValue(sourceStatus) !== 'in_progress'
  ) {
    nextStatus = 'in_progress';
    derivedFromAppointments = true;
  }

  return {
    status: nextStatus,
    sourceStatus,
    appointmentCount: appointmentSources.length,
    activeAppointmentCount,
    derivedFromAppointments,
  };
}

export function summarizeTeamScopeFromFinancialSummary(payload: {
  success?: boolean;
  data?: {
    source_breakdown?: Array<{
      id?: string | null;
      item_number?: string | null;
      item_type?: string;
      assigned_team_name?: string | null;
    }>;
  };
} | null): TeamScopeSnapshot | null {
  const sourceBreakdown = Array.isArray(payload?.data?.source_breakdown)
    ? dedupeRecords(payload.data.source_breakdown, (source, index) => (
      source.id || source.item_number || `${source.item_type}:${source.assigned_team_name || ''}:${index}`
    ))
    : [];

  const appointmentSources = sourceBreakdown.filter((source) => source?.item_type === 'appointment');
  const teamNames = Array.from(new Set(
    appointmentSources
      .map((source) => String(source?.assigned_team_name || '').trim())
      .filter(Boolean),
  ));

  if (appointmentSources.length === 0 && teamNames.length === 0) {
    return null;
  }

  return {
    total_appointments: appointmentSources.length,
    team_assigned_appointments: appointmentSources.filter((source) => String(source?.assigned_team_name || '').trim()).length,
    team_names: teamNames,
  };
}

export function summarizeTeamScopeFromTimeEntries(payload: {
  success?: boolean;
  appointment_breakdown?: Array<{
    source_work_item_id?: string | null;
    item_number?: string | null;
    assigned_team_name?: string | null;
  }>;
} | null): TeamScopeSnapshot | null {
  const appointmentBreakdown = Array.isArray(payload?.appointment_breakdown)
    ? dedupeRecords(payload.appointment_breakdown, (appointment, index) => (
      appointment.source_work_item_id || appointment.item_number || `${appointment.assigned_team_name || ''}:${index}`
    ))
    : [];

  const teamNames = Array.from(new Set(
    appointmentBreakdown
      .map((appointment) => String(appointment?.assigned_team_name || '').trim())
      .filter(Boolean),
  ));

  if (appointmentBreakdown.length === 0 && teamNames.length === 0) {
    return null;
  }

  return {
    total_appointments: appointmentBreakdown.length,
    team_assigned_appointments: appointmentBreakdown.filter((appointment) => String(appointment?.assigned_team_name || '').trim()).length,
    team_names: teamNames,
  };
}

export function openLinkedWorkItem(id: string) {
  if (typeof window === 'undefined') {
    return;
  }

  emit('openWorkItem', { id });
}

export function getDocumentStatusPillClass(status: string, isDark: boolean): string {
  const normalized = String(status || '').toLowerCase();

  if (normalized === 'draft') {
    return isDark
      ? 'bg-slate-700/40 text-slate-300 border border-slate-600/50'
      : 'bg-slate-100 text-slate-600 border border-slate-200';
  }

  if (normalized === 'pending_approval') {
    return isDark
      ? 'bg-yellow-900/30 text-yellow-300 border border-yellow-800/50'
      : 'bg-yellow-50 text-yellow-700 border border-yellow-200';
  }

  if (normalized === 'approved' || normalized === 'ready_to_send') {
    return isDark
      ? 'bg-emerald-900/30 text-emerald-300 border border-emerald-800/50'
      : 'bg-emerald-50 text-emerald-700 border border-emerald-200';
  }

  if (normalized === 'rejected') {
    return isDark
      ? 'bg-red-900/30 text-red-300 border border-red-800/50'
      : 'bg-red-50 text-red-700 border border-red-200';
  }

  if (normalized === 'converted') {
    return isDark
      ? 'bg-teal-900/30 text-teal-300 border border-teal-800/50'
      : 'bg-teal-50 text-teal-700 border border-teal-200';
  }

  if (normalized === 'cancelled') {
    return isDark
      ? 'bg-rose-900/30 text-rose-300 border border-rose-800/50'
      : 'bg-rose-50 text-rose-700 border border-rose-200';
  }

  return isDark
    ? 'bg-slate-800 text-slate-300 border border-slate-700'
    : 'bg-slate-50 text-slate-700 border border-slate-200';
}

export function getBillingSubmissionStatusLabel(status: string): string {
  const normalized = String(status || '').toLowerCase();

  if (normalized === 'ready_to_send') {
    return 'Ready to Send';
  }

  if (Object.prototype.hasOwnProperty.call(BILLING_SUBMISSION_STATUS_LABELS, normalized)) {
    return BILLING_SUBMISSION_STATUS_LABELS[normalized as BillingSubmissionStatus];
  }

  return normalized.replace(/_/g, ' ');
}

export function sectionNeedsOverview(section?: BillingSectionId): boolean {
  return section === 'costing' || section === 'time' || section === 'maintenance' || section === 'submissions';
}
